import type { FC } from 'react'
import toast from 'react-hot-toast'
import { Chip } from '@/components/commons/chip'
import { FlipBackwardIcon, XCloseIcon } from '@/components/ui/icons'
import { useAddImagesFlag } from '@/features/photoset/queries/use-add-images-flag'
import { useRemoveImagesFlag } from '@/features/photoset/queries/use-remove-images-flag'
import {
	type Flag,
	type Image,
	type PhotosetSpace,
	StepKey
} from '@/features/photoset/types'
import { getFlagVariant } from '@/features/photoset/utils'
import { useTranslation } from '@/translations/i18n'

interface EditingQaChipProps {
	flag: Flag
	image: Omit<Image, 'space'>
	photosetSpaceIds: PhotosetSpace['id'][]
	label: string
	isQaView: boolean
}

export const EditingQaChip: FC<EditingQaChipProps> = ({
	flag,
	image,
	photosetSpaceIds,
	label,
	isQaView
}) => {
	const { t } = useTranslation()

	const variant = getFlagVariant(flag.id, image.image_flags)

	const addImagesFlag = useAddImagesFlag({
		photosetSpaceIds,
		stepId: StepKey['editing-qa-flags'],
		options: {
			onError: () => toast.error(t('errors:default'))
		}
	})

	const removeImagesFlag = useRemoveImagesFlag({
		photosetSpaceIds,
		stepId: StepKey['editing-qa-flags'],
		options: {
			onError: () => toast.error(t('errors:default'))
		}
	})

	if (!variant) {
		return
	}

	return (
		<Chip
			endSlot={
				isQaView && (
					<button
						disabled={
							addImagesFlag.isPending ||
							removeImagesFlag.isPending
						}
						onClick={() =>
							variant === 'danger'
								? addImagesFlag.mutate({
										flag,
										imageIds: [image.id]
									})
								: removeImagesFlag.mutate({
										flag,
										imageIds: [image.id]
									})
						}
						tabIndex={-1}
						type='button'
					>
						{variant === 'danger' ? (
							<FlipBackwardIcon />
						) : (
							<XCloseIcon />
						)}
					</button>
				)
			}
			isActive={variant !== 'neutral'}
			key={flag.id}
			label={label}
			variant={variant}
		/>
	)
}
